<div class="manual-appointment-wrapper">
  <!-- Loading overlay -->
  @if (isLoading) {
  <div
    class="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
    style="background: rgba(255, 255, 255, 0.6); z-index: 10"
  >
    <div class="spinner-border" role="status" aria-hidden="true"></div>
  </div>
  }

  <c-card class="modern-card">
    <!-- Dark header -->
    <div class="header-minimal">
      <span class="eyebrow text-uppercase">
        {{ "MANUAL_APPOINTMENT.EYEBROW" | translate }}
      </span>
      <h2 class="company-name mb-0">
        {{ companyName || "Sagel" }}
      </h2>
    </div>

    <!-- Summary bar -->
    <div class="summary-bar">
      <div class="summary-item">
        <small>{{ "MANUAL_APPOINTMENT.COMPANY_LABEL" | translate }}</small>
        <span>{{ companyName || "Sagel" }}</span>
      </div>
      <div class="summary-item">
        <small>{{ "MANUAL_APPOINTMENT.MODE_LABEL" | translate }}</small>
        <span>{{ "MANUAL_APPOINTMENT.MODE_VALUE" | translate }}</span>
      </div>
      <div class="summary-item">
        <small>{{ "MANUAL_APPOINTMENT.TODAY_LABEL" | translate }}</small>
        <span>{{ today | date : "MMM d, y" }}</span>
      </div>
    </div>

    <c-card-body class="body-content">
      <form (ngSubmit)="onSubmit()" class="modern-form">
        <!-- Step pills -->
        <div class="step-progress">
          <button
            type="button"
            class="step-pill"
            [ngClass]="{
              active: currentStep === 1,
              complete: isStepOneValid()
            }"
            (click)="goToStep(1)"
          >
            <span class="step-number">1</span>
            <span class="step-label">{{
              "MANUAL_APPOINTMENT.STEP_1_LABEL" | translate
            }}</span>
          </button>
          <button
            type="button"
            class="step-pill"
            [ngClass]="{
              active: currentStep === 2,
              complete: isStepTwoValid()
            }"
            [disabled]="!canAccessStep(2)"
            (click)="goToStep(2)"
          >
            <span class="step-number">2</span>
            <span class="step-label">{{
              "MANUAL_APPOINTMENT.STEP_2_LABEL" | translate
            }}</span>
          </button>
        </div>

        <div class="step-note mb-3">
          {{
            "MANUAL_APPOINTMENT.STEP_NOTE"
              | translate : { current: currentStep, total: totalSteps }
          }}
        </div>

        @if (stepValidationMessage) {
        <div class="step-error mb-3">
          {{ stepValidationMessage }}
        </div>
        }

        <!-- ── Step 1: Date, Time & Meeting link ────────────────────────── -->
        @if (currentStep === 1) {
        <div class="step-panel">
          <div class="row g-4">
            <div class="col-12 col-md-6">
              <label class="form-label">
                {{ "MANUAL_APPOINTMENT.DATE" | translate }}
              </label>
              <c-date-picker
                closeOnSelect
                [(ngModel)]="startDate"
                locale="en-GB"
                format="dd/MM/yyyy"
                name="startDate"
                (ngModelChange)="stepValidationMessage = ''"
                required
              ></c-date-picker>
            </div>
            <div class="col-12 col-md-6">
              <label class="form-label">
                {{ "MANUAL_APPOINTMENT.TIME" | translate }}
              </label>
              <select
                class="form-select"
                [(ngModel)]="startTime"
                name="startTime"
                (ngModelChange)="stepValidationMessage = ''"
                required
              >
                <option value="" disabled>
                  {{ "MANUAL_APPOINTMENT.TIME" | translate }}
                </option>
                @for (t of timeOptions; track t) {
                <option [value]="t">{{ t }}</option>
                }
              </select>
            </div>
          </div>
          <!-- Meeting link toggle -->
          <div class="meeting-section mt-4">
            <label class="meeting-toggle-label">
              <input
                type="checkbox"
                [(ngModel)]="addMeetingLink"
                name="addMeetingLink"
              />
              <span>{{
                "MANUAL_APPOINTMENT.ADD_MEETING_LINK" | translate
              }}</span>
            </label>
            @if (addMeetingLink) {
            <div class="meeting-options mt-3">
              <div class="mode-tabs">
                <button
                  type="button"
                  class="mode-tab"
                  [class.mode-tab--active]="meetingLinkMode === 'generate'"
                  (click)="meetingLinkMode = 'generate'"
                >
                  {{ "MANUAL_APPOINTMENT.MEETING_GENERATE" | translate }}
                </button>
                <button
                  type="button"
                  class="mode-tab"
                  [class.mode-tab--active]="meetingLinkMode === 'manual'"
                  (click)="meetingLinkMode = 'manual'"
                >
                  {{ "MANUAL_APPOINTMENT.MEETING_MANUAL" | translate }}
                </button>
              </div>
              @if (meetingLinkMode === 'generate') {
              <div class="meeting-preview mt-2">
                <small class="meeting-preview__label">{{
                  "MANUAL_APPOINTMENT.MEETING_PREVIEW" | translate
                }}</small>
                <code class="meeting-preview__url">{{
                  generatedMeetingUrl || "—"
                }}</code>
              </div>
              } @if (meetingLinkMode === 'manual') {
              <div class="mt-2">
                <input
                  type="url"
                  [(ngModel)]="manualMeetingLink"
                  name="manualMeetingLink"
                  class="form-control"
                  placeholder="https://..."
                />
              </div>
              }
            </div>
            }
          </div>
        </div>
        }

        <!-- ── Step 2: Contact info ─────────────────────────────────────── -->
        @if (currentStep === 2) {
        <div class="step-panel">
          <div class="row g-4 mt-3">
            <div class="col-12 col-md-6">
              <label class="form-label">
                {{ "MANUAL_APPOINTMENT.NAME" | translate }}
              </label>
              <input
                type="text"
                class="form-control"
                [(ngModel)]="name"
                name="name"
                (ngModelChange)="stepValidationMessage = ''"
                required
              />
            </div>
            <div class="col-12 col-md-6">
              <label class="form-label">
                {{ "MANUAL_APPOINTMENT.EMAIL" | translate }}
              </label>
              <input
                type="email"
                class="form-control"
                [(ngModel)]="email"
                name="email"
                (ngModelChange)="stepValidationMessage = ''"
                required
              />
            </div>
          </div>
          <div class="row g-4 mt-3">
            <div class="col-12 col-md-6">
              <label class="form-label">
                {{ "MANUAL_APPOINTMENT.PHONE" | translate }}
              </label>
              <input
                type="tel"
                class="form-control"
                [(ngModel)]="phone"
                name="phone"
              />
            </div>
          </div>
          <div class="row g-4 mt-3">
            <div class="col-12">
              <label class="form-label">
                {{ "MANUAL_APPOINTMENT.NOTES" | translate }}
              </label>
              <textarea
                class="form-control"
                [(ngModel)]="notes"
                name="notes"
                rows="3"
              ></textarea>
            </div>
          </div>
        </div>
        }

        <!-- Step actions -->
        <div class="step-actions">
          <button
            cButton
            type="button"
            class="btn-nav-modern"
            [disabled]="currentStep === 1"
            (click)="prevStep()"
          >
            {{ "MANUAL_APPOINTMENT.STEP_BACK" | translate }}
          </button>

          @if (currentStep < totalSteps) {
          <button
            cButton
            type="button"
            class="btn-nav-modern btn-next-modern"
            (click)="nextStep()"
          >
            {{ "MANUAL_APPOINTMENT.STEP_NEXT" | translate }}
          </button>
          } @if (currentStep === totalSteps) {
          <button
            cButton
            type="submit"
            class="btn-submit-modern"
            [disabled]="isSubmitting"
          >
            @if (isSubmitting) {
            <span
              class="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            ></span>
            }
            {{
              isSubmitting
                ? ("MANUAL_APPOINTMENT.SUBMITTING" | translate)
                : ("MANUAL_APPOINTMENT.SUBMIT" | translate)
            }}
          </button>
          }
        </div>
      </form>
    </c-card-body>

    <c-card-footer class="powered-sagel text-center">
      <small class="powered-text text-uppercase">
        {{ "MANUAL_APPOINTMENT.POWERED_BY" | translate }}
        <span class="text-accent fw-semibold">Sagel</span>
      </small>
      <img
        src="assets/img/brand/SAGEL_SMALL.svg"
        alt="Sagel logo"
        class="powered-logo ms-2"
      />
    </c-card-footer>
  </c-card>

  <!-- ── Result modal overlay ─────────────────────────────────────────────── -->
  @if (showSavedNotification || showErrorNotification) {
  <div class="modal-overlay">
    <div
      class="modal-card"
      [ngClass]="showSavedNotification ? 'success' : 'danger'"
    >
      <div class="modal-status-icon">
        @if (showSavedNotification) {
        <i class="cil-check-circle"></i>
        } @if (showErrorNotification) {
        <i class="cil-x-circle"></i>
        }
      </div>
      <h3 class="modal-title">
        {{
          showSavedNotification
            ? ("MANUAL_APPOINTMENT.ALERT_SUCCESS_HEADING" | translate)
            : ("MANUAL_APPOINTMENT.ALERT_ERROR_HEADING" | translate)
        }}
      </h3>
      @if (showSavedNotification) {
      <p class="modal-message">
        {{
          "MANUAL_APPOINTMENT.ALERT_SUCCESS_MESSAGE"
            | translate
              : {
                  reservationId: reservationId,
                  name: name,
                  date: formattedStartDate,
                  time: startTime
                }
        }}
      </p>
      } @if (showErrorNotification) {
      <p class="modal-message">
        {{ errorText }}
      </p>
      } @if (showSavedNotification) {
      <p class="modal-hint">
        {{ "MANUAL_APPOINTMENT.ALERT_SUCCESS_ADVICE" | translate }}
      </p>
      }
      <button type="button" class="modal-action" (click)="onCloseAlert()">
        {{ "MANUAL_APPOINTMENT.ALERT_OK" | translate }}
      </button>
    </div>
  </div>
  }
</div>
